/**
 * Pure helpers for how a mech looks: the livery values and the cosmetic parts to show. Kept free
 * of Three.js so the unit tests and the shared loadout code can use them.
 */
import type { MechLiveryDefaults, MechSidecar } from '@steelwake/shared/data';
import { liveryOf, partsOf, type Loadout } from '@steelwake/shared/loadout';

export interface MechLook {
  livery: MechLiveryDefaults;
  /** Cosmetic part ids to show; every other `COSMETIC_*` node stays hidden. */
  parts: readonly string[];
  /** Show the `WEAPON_ALT_primary` model instead of the stock primary (Phase 8 weapon swaps). */
  alternatePrimary: boolean;
}

/** Cosmetic parts are exported as `COSMETIC_<partId>` plus `COSMETIC_<partId>-R` for a mirror. */
export const COSMETIC_PREFIX = 'COSMETIC_';

export function partIdOfNode(name: string): string | null {
  if (!name.startsWith(COSMETIC_PREFIX)) return null;
  const rest = name.slice(COSMETIC_PREFIX.length);
  if (rest.length === 0) return null;
  return rest.endsWith('-R') ? rest.slice(0, -2) : rest;
}

/** The look the recipe gave a mech: its default livery and every cosmetic it was assembled with. */
export function defaultLook(sidecar: MechSidecar): MechLook {
  const parts = new Set<string>();
  for (const node of sidecar.cosmetics) {
    const id = partIdOfNode(node);
    if (id !== null) parts.add(id);
  }
  return { livery: sidecar.livery, parts: [...parts], alternatePrimary: false };
}

/** The loadout's choices over the mech's stock look; null keeps the stock look. */
export function lookFor(sidecar: MechSidecar, loadout: Loadout | null): MechLook {
  const stock = defaultLook(sidecar);
  if (!loadout) return stock;
  return {
    livery: liveryOf(stock.livery, loadout),
    parts: partsOf(stock.parts, loadout),
    alternatePrimary:
      loadout.weapons.primary !== null &&
      loadout.weapons.primary === sidecar.weapons.alternatePrimary,
  };
}

/** UV offset of a tile in a square atlas laid out row-major from the top-left (glTF convention). */
export function decalTileOffset(index: number, columns: number): [number, number] {
  const col = index % columns;
  const row = Math.floor(index / columns);
  return [col / columns, row / columns];
}
